import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError(t("reset.mismatch")); return; }
    if (pw.length < 6) { setError(t("reset.tooShort")); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("reset.errorGeneric"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4 py-12 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div data-testid="reset-password-page" className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
          <div className="w-12 h-12 rounded-md bg-zinc-100 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-zinc-700" />
          </div>
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">PromoCrew</div>
            <div className="font-display font-bold text-lg text-zinc-950">{t("reset.title")}</div>
          </div>
        </div>

        {!token ? (
          <div data-testid="reset-no-token" className="text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
            <div className="text-sm text-zinc-600">{t("reset.missingToken")} <Link to="/forgot-password" className="text-zinc-900 underline">{t("reset.missingTokenLink")}</Link>.</div>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <div className="text-sm font-medium text-zinc-900">{t("reset.successTitle")}</div>
            <div className="text-xs text-zinc-500">{t("reset.successRedirect")}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-zinc-600">{t("reset.help")}</p>
            {error && (
              <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("reset.newPassword")}</label>
              <input data-testid="reset-password-new" required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 text-sm" placeholder="••••••" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("reset.confirm")}</label>
              <input data-testid="reset-password-confirm" required type="password" minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 text-sm" placeholder="••••••" />
            </div>
            <button data-testid="reset-submit" type="submit" disabled={submitting} className="w-full bg-zinc-950 hover:bg-zinc-800 text-white font-semibold py-2.5 rounded-lg disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {submitting ? t("reset.saving") : t("reset.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
